import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogAPI } from '../services/api';
import { Calendar, Clock, Eye, Tag, ArrowRight, Search } from 'lucide-react';
import { formatDateDisplay } from '../lib/dateUtils';

import { FlutterFoxFooter } from '../components/FlutterFoxFooter';

export default function BlogList() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    blogAPI.listPublic({ page, limit: 9, tag: tag || undefined })
      .then(r => { setPosts(r.data.posts || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, tag]);

  return (
    <div className="bg-slate-950 text-white min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Nav */}
      <nav className="bg-slate-950/95 backdrop-blur-md border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-3">
            <img src="/images/transsolution_logo.png" alt="Transsolution" className="w-8 h-8 rounded object-contain bg-white p-0.5" />
            <span className="font-bold text-white text-sm tracking-tight" style={{ fontFamily: 'Barlow, sans-serif' }}>TRANSSOLUTION</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Home</a>
            <a href="/about" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">About</a>
            <a href="/contact" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Contact</a>
            <button onClick={() => navigate('/login')} className="text-sm text-slate-300 hover:text-white px-4 py-2">Login</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="blog-hero">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>Blog</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
            INSIGHTS FOR LOGISTICS
          </h1>
          <p className="text-base text-slate-400 max-w-xl mx-auto">GST guides, accounting tips, and industry insights for Indian transport companies.</p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="pb-20" data-testid="blog-posts">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16"><p className="text-slate-500 text-lg">No blog posts yet. Check back soon!</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <a key={post.post_id} href={`/blog/${post.slug}`}
                  className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-orange-500/30 transition-all group"
                  data-testid={`blog-card-${post.slug}`}>
                  {post.cover_image && (
                    <div className="h-40 overflow-hidden">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <div className="p-5">
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {post.tags.slice(0, 3).map((t, i) => (
                          <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-600/10 text-orange-400">{t}</span>
                        ))}
                      </div>
                    )}
                    <h2 className="font-bold text-base text-slate-100 group-hover:text-orange-400 transition-colors mb-2 line-clamp-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
                      {post.title}
                    </h2>
                    <p className="text-sm text-slate-400 line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {formatDateDisplay(post.published_at || post.created_at)}</span>
                        {post.views > 0 && <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>}
                      </div>
                      <span className="text-orange-500 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight size={12} /></span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 9 && (
            <div className="flex justify-center gap-2 mt-10">
              {Array.from({ length: Math.ceil(total / 9) }, (_, i) => (
                <button key={i} onClick={() => setPage(i + 1)}
                  className={`w-8 h-8 rounded-md text-sm font-medium ${page === i + 1 ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-4">
          <span className="text-xs text-slate-500">&copy; 2026 Transsolution ERP.</span>
          <FlutterFoxFooter testId="blog-list-footer" />
        </div>
      </footer>
    </div>
  );
}
